import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const guideLinks = [
  { label: 'Ultimate CSSBuy Guide', href: '/article/cssbuy-spreadsheet-ultimate-guide' },
  { label: 'CSSBuy vs Kakobuy', href: '/article/cssbuy-vs-kakobuy-comparison' },
  { label: 'Shipping Guide', href: '/article/cssbuy-shipping-guide' },
  { label: 'Seller Directory', href: '/article/cssbuy-seller-directory' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              CSS<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line"></i>
                Categories
                <i className={`ri-arrow-down-s-line transition-transform text-xs ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-primary-500 text-lg w-5 h-5 flex items-center justify-center`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={guidesRef}>
              <button
                onClick={() => setGuidesOpen(!guidesOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                Guides
                <i className={`ri-arrow-down-s-line transition-transform text-xs ${guidesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guidesOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  {guideLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-article-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a href="#faq" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              FAQ
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              CSSBuy Sheet
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={mobileOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-background-50 overflow-y-auto">
            <div className="max-w-7xl mx-auto px-4 py-5">
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide px-2 mb-1">Categories</p>
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                  >
                    <i className={`${cat.icon} text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                    {cat.name}
                  </a>
                ))}
                <div className="border-t border-background-200/70 my-2"></div>
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide px-2 mb-1">Guides</p>
                {guideLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-background-200/70 my-2"></div>
                <a href="#faq" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors">FAQ</a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="mt-2 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold text-center hover:bg-primary-600 transition-all"
                >
                  CSSBuy Sheet
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}